import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';
import { BuyerDocumentResponseDto } from '../dto/response/buyer-document-response.dto';
import buyerDocumentService from '../services/buyer-document.service';
import { BuyerDocumentUpdateRequestDto } from '../dto/request/buyer-document-update-request.dto';
import { BuyerDocumentSaveRequestDto } from '../dto/request/buyer-document-save-request.dto';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  documents: [] as BuyerDocumentResponseDto[] | null,
  document: {} as BuyerDocumentResponseDto | null,
  error: null as string | null,
};

export const fetchDocumentById = createAsyncThunk(
  'buyerDocument/fetchBuyerDocumentById',
  async (params: {
    documentId: string;
    language: LocalesLanguagesConstants;
  }) => {
    const { documentId, language } = params;
    return await buyerDocumentService.fetchDocumentById(documentId, language);
  }
);

export const updateDocument = createAsyncThunk(
  'buyerDocument/updateBuyerDocument',
  async (
    params: {
      data: BuyerDocumentUpdateRequestDto;
      language: LocalesLanguagesConstants;
    },
    { rejectWithValue }
  ) => {
    const { data, language } = params;
    try {
      return await buyerDocumentService.updateDocument(data, language);
    } catch (e) {
      return rejectWithValue(
        ((e as AxiosError).response?.data as any).message[0] ?? 'Failed'
      );
    }
  }
);

export const saveBuyerDocument = createAsyncThunk(
  'buyerDocument/saveBuyerDocument',
  async (
    params: {
      data: BuyerDocumentSaveRequestDto;
      language: LocalesLanguagesConstants;
    },
    { rejectWithValue }
  ) => {
    const { data, language } = params;
    try {
      return await buyerDocumentService.saveBuyerDocument(data, language);
    } catch (e) {
      return rejectWithValue(
        ((e as AxiosError).response?.data as any).message[0] ?? 'Failed'
      );
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'buyerDocument/deleteBuyerDocument',
  async (id: string, { rejectWithValue }) => {
    try {
      await buyerDocumentService.deleteDocument(id);
    } catch (e) {
      return rejectWithValue(
        ((e as AxiosError).response?.data as any).message[0] ?? 'Failed'
      );
    }
  }
);

const buyerDocumentSlice = createSlice({
  name: 'buyerDocumentSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDocumentById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.document = action.payload;
      })
      .addCase(fetchDocumentById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveBuyerDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveBuyerDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess('Organization added successfully.');
        state.documents?.push(action.payload);
        state.document = action.payload;
      })
      .addCase(saveBuyerDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Adding document failed. ${action.payload as string}`);
      })
      .addCase(updateDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.documents) {
          const documentIndex = state.documents.findIndex(
            (document) => document.id === action.payload.id
          );
          if (documentIndex !== -1) {
            state.documents[documentIndex] = action.payload;
          }
        }
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Saving document failed. ${action.payload as string}`);
      })
      .addCase(deleteDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteDocument.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Organization deleted successfuly');
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting document failed. ${action.payload as string}`);
      });
  },
});

export default buyerDocumentSlice.reducer;
